// import dayjs from "dayjs";
// import { ENTRIES_PER_PAGE } from "@/constants";
import { IVideoProfile } from "@/app/etablissement/un-detail-pour-vous/page";
import { IBanner } from "@/types/banner";
import { IEvent } from "@/types/event";
import { IHomepage } from "@/types/homepage";
import { IOperation } from "@/types/operation";
import { IPage } from "@/types/page";
import { IPost } from "@/types/post";
import { IProfile } from "@/types/profile";
import dayjs from "dayjs";

const LINKS_GRAPHQL = `
links {
  assets {
    block {
      url
      title
      contentType
      fileName
      sys {
        id
      }
    }
  }
  entries {
    block{
      ... on Profile{
        __typename
        sys{ id }
        firstname lastname position
        image{ url title }
        body{ json }
        email
      }
      ... on Nav {
        __typename
        sys{ id }
        #appearance
        title
        entriesCollection {
          items {
            sys{ id }
            title
            slug
          }
        }
      }  
      ... on ProfilesList {
        __typename
        sys{ id }
        appearance
        profilesCollection {
          items {
            sys{ id }
            firstname
            lastname
            position
            email
            body{ json }
            image {
              url
              title
            }
          }
        }
      }        
      ... on Calendar{
        __typename
        sys{ id }
        calendarData
      }
      ... on Video{
        __typename
        sys{ id }
        title
        youtubeUrl
      }
      ... on Gallery{
        __typename
        sys{ id }
        imagesCollection{
          items{
            url
            title
            width
            height
          }
        }
      }
    }
    hyperlink {
      ... on Gallery {
        __typename
        title
        imagesCollection{
          items{
            url
            width
            height
          }
        }
      }
      ... on Calendar {
        __typename
        calendarData
      }
      ... on Video{
        __typename
        youtubeUrl
      }
      ... on Page {
        __typename
        title
        slug
        sys {
          id
        }
      }
    }
  }
}`;

async function fetchGraphQL(
  query: string,
  preview: boolean = false
): Promise<any> {
  return fetch(
    `https://graphql.contentful.com/content/v1/spaces/${process.env.CF_SPACE_ID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${
          preview
            ? process.env.CF_PREVIEW_ACCESS_TOKEN
            : process.env.CF_LIVE_ACCESS_TOKEN
        }`,
      },
      body: JSON.stringify({ query }),
      next: {
        //revalidate: process.env.NODE_ENV === `development` ? 0 : 3600 * 24 * 30,
        // tags: ['posts']
      },
    }
  )
    .then((response) => response.json())
    .catch((error) => console.log("error GraphQL", error));
}

export async function getAllOperations(
  isDraftMode: boolean = false
): Promise<any> {
  const request = await fetchGraphQL(
    `
        query {
          operationCollection(
              preview: ${isDraftMode ? "true" : "false"}
              where: { slug_exists: true }
              limit: 200
          ) {
            items{
              __typename
              sys { id }
              title
              slug
              city
              direction
              type
              intro
              location{ lat lon }
              area
              image{ width height url title }
            }
          }
        }
        `,
    isDraftMode
  );
  return request.data?.operationCollection?.items as IOperation[];
}

export async function getOneOperation(
  isDraftMode: boolean = false,
  slug: string = ``
): Promise<any> {
  const request = await fetchGraphQL(
    `
          query {
            operationCollection(
                preview: ${isDraftMode ? "true" : "false"}
                limit: 1
                where: {slug: "${slug}"}
            ) {
              items{
                __typename
                sys{ id publishedAt }
                title
                slug
                city
                type
                direction
                intro
                location{ lat lon }
                keyIndicators
                area
                body{ json ${LINKS_GRAPHQL} }
                image{
                  width
                  height
                  url
                  title
                }
              }
            }
          }
          `,
    isDraftMode
  );
  return request.data?.operationCollection?.items.pop() as IOperation[];
}

export async function getAllPublications(
  isDraftMode: boolean = false,
  limit: number,
  skip: number
): Promise<any> {
  const request = await fetchGraphQL(
    `
          query {
            pageCollection(
                preview: ${isDraftMode ? "true" : "false"}
                ${limit ? `limit: ${limit}` : ``}
                ${skip ? `skip: ${skip}` : ``}
                where:{ contentfulMetadata:{ tags: { id_contains_none: "corporate"}}}
            ) {
              total
              items{
                __typename
                sys{ id publishedAt }
                #contentfulMetadata{
                #  tags{
                #    name id
                #  }
                #}
                publicationDate
                image{ sys { id }}
                title
                intro
                slug
                image{ url width height title }
              }
            }
          }
          `,
    isDraftMode
  );
  return request.data?.pageCollection?.items as IPost[];
}

export async function getAllFilteredPublications(
  isDraftMode: boolean = false,
  limit: number,
  skip: number,
  types: string[]
): Promise<any> {
  const request = await fetchGraphQL(
    `
          query {
            pageCollection(
                preview: ${isDraftMode ? "true" : "false"}
                ${limit ? `limit: ${limit}` : ``}
                ${skip ? `skip: ${skip}` : ``}
                where:{
                  contentfulMetadata:{
                    tags: { id_contains_some: ${types.join(`,`)} }
                  }
                }
            ) {
              total
              items{
                __typename
                sys{ id publishedAt }
                contentfulMetadata{
                  tags{
                    name id
                  }
                }
                title
                slug
                image{ url width height title }
              }
            }
          }
          `,
    isDraftMode
  );
  return request.data?.pageCollection?.items as IPost[];
}

export async function getCountPublications(
  isDraftMode: boolean = false
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        pageCollection(
            preview: ${isDraftMode ? "true" : "false"}
            where:{ contentfulMetadata:{ tags: { id_contains_none: "corporate"}}}
        ) {
          total
        }
      }
      `,
    isDraftMode
  );
  return request.data?.pageCollection?.total as number;
}

export async function getOnePage(
  isDraftMode: boolean = false,
  slug: string = ``
): Promise<any> {
  const request = await fetchGraphQL(
    `
  query {
    pageCollection(
        preview: ${isDraftMode ? "true" : "false"}
        limit: 1
        where:{ slug: "${slug}" }
    ) {
      items{
        __typename
        linkedFrom{
          entryCollection{
            items{
              sys{ id }
            }
          }
        }
        sys{ id publishedAt }
        contentfulMetadata{
          tags{
            name id
          }
        }
        refParent{
          ...on Page{
              title
              slug
            }
        }
        publicationDate
        title
        seoTitle
        seoDescription
        intro
        slug
        singleColumnLayout
        keyIndicators
        image{
          sys{ id }
          url
          width
          height
          title
        }
        body{ json ${LINKS_GRAPHQL} }
        sidebarBody{ json ${LINKS_GRAPHQL} }
      }
    }
  }
`,
    isDraftMode
  );

  return request.data?.pageCollection?.items.pop() as IPost;
}

export async function getAllCorporatePage(
  isDraftMode: boolean = false
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        pageCollection(
            preview: ${isDraftMode ? "true" : "false"}
            where:{ contentfulMetadata:{ tags: { id_contains_all: "corporate"}}}
            order: position_ASC
        ) {
          total
          items{
            __typename
            sys{ id publishedAt }
            contentfulMetadata{
              tags{
                name id
              }
            }
            title
            slug
            hidePageFromCorporateNav
            image{ url width height title }
          }
        }
      }
      `,
    isDraftMode
  );
  return request.data?.pageCollection?.items as IPost[];
}

export async function getAllDaughtersPage(
  isDraftMode: boolean = false,
  parentId: string
): Promise<any> {
  const request = await fetchGraphQL(
    `
    query {
      pageCollection(
          preview: ${isDraftMode ? "true" : "false"}
          where:{ refParent_exists: true }
          order: position_ASC
      ) {
        total
        items{
          __typename
          sys{ id publishedAt }
          refParent{ sys{ id } }
          contentfulMetadata{
            tags{
              name id
            }
          }
          title
          slug
          image{ url width height title }
        }
      }
    }
    `,
    isDraftMode
  );
  return request.data?.pageCollection?.items.filter(
    (page: IPage) => page.refParent.sys.id === parentId
  ) as IPage[];
}

export async function getHomepage(isDraftMode: boolean = false): Promise<any> {
  const request = await fetchGraphQL(
    `
    query {
      homeCollection(preview: false, limit: 1) {
        items {
          __typename
          sys {
            id
            publishedAt
          }
          title
          seoTitle
          seoDescription
          image {
            url
            height
            width
            title
          }
          featuredEntry{
            ... on Page {
              sys{ id }
              slug
              title
              image{ url title }
            }
            ... on Post {
              sys { id }
              slug
              title
              image{ url title }
            }
          }
          contentsIsCollectivityCollection {
            total
            items {
              __typename
              ... on Post {
                __typename
                sys {
                  id
                }
                title
                slug
                image {
                  url
                }
              }
              ... on Page {
                __typename
                sys{ id }
                title
                slug
                image {
                  url
                }
              }
            }
          }
          contentsIsIndividualCollection {
            items {
              __typename
              ... on Post {
                __typename
                sys {
                  id
                }
                title
                slug
                image {
                  url
                }
              }
              ... on Page {
                __typename
                sys{ id }
                title
                slug
                image {
                  url
                }
              }
            }
          }
          contentsIsCandidateCollection {
            items {
              __typename
              ... on Post {
                __typename
                sys {
                  id
                }
                title
                slug
                image {
                  url
                }
              }
              ... on Page {
                __typename
                sys{ id }
                title
                slug
                image {
                  url
                }
              }
            }
          }
          contentsIsJournalistCollection {
            items {
              __typename
              ... on Post {
                __typename
                sys {
                  id
                }
                title
                slug
                image {
                  url
                }
              }
              ... on Page {
                __typename
                sys{ id }
                title
                slug
                image {
                  url
                }
              }
            }
          }
          featuredOperationsCollection {
            items {
              ... on Operation {
                __typename
                sys{ id }
                slug
                title
                city
                image {
                  url
                }
              }
            }
          }
        }
      }
    } 
  `,
    isDraftMode
  );
  return request.data?.homeCollection?.items.pop() as IHomepage;
}

export async function getAllEvents(isDraftMode: boolean = false): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        eventCollection(
            preview: ${isDraftMode ? "true" : "false"}
        ) {
          total
          items{
            __typename
            title
            intro
            slug
            startDate
            endDate
            sys{ id publishedAt firstPublishedAt }
            image{ url title}
            #body{ json }
          }
        }
      }
      `,
    isDraftMode
  );
  return request.data?.eventCollection?.items as IEvent[];
}

export async function getAllPosts(
  isDraftMode: boolean = false,
  limit: number,
  skip: number
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        postCollection(
            preview: ${isDraftMode ? "true" : "false"}
            order: publicationDate_DESC
            ${limit ? `limit: ${limit}` : ``}
            ${skip ? `skip: ${skip}` : ``}
        ) {
          total
          items{
            __typename
            contentfulMetadata{ tags{ name id } }
            sys{ id publishedAt firstPublishedAt }
            title
            slug
            publicationDate
            intro
            image{ url title}
          }
        }
      }
      `,
    isDraftMode
  );
  return request.data?.postCollection?.items as IPost[];
}

export async function getOnePost(
  isDraftMode: boolean = false,
  slug: string
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        postCollection(
            preview: ${isDraftMode ? "true" : "false"}
            limit: 1
            where:{ slug: "${slug}" }
        ) {
          total
          items{
            __typename
            sys{ id publishedAt firstPublishedAt }
            contentfulMetadata{ tags{ name id } }
            title
            slug
            publicationDate
            intro
            image{ url title }
            body{ json ${LINKS_GRAPHQL} }
          }
        }
      }
    `,
    isDraftMode
  );
  return request.data?.postCollection?.items.pop() as IPost;
}

export async function getCountPosts(
  isDraftMode: boolean = false
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        postCollection(
            preview: ${isDraftMode ? "true" : "false"}
        ) {
          total
        }
      }
      `,
    isDraftMode
  );
  return request.data?.postCollection?.total as number;
}

export async function getAllProfiles(
  isDraftMode: boolean = false,
  limit: number
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        profileCollection(
            preview: ${isDraftMode ? "true" : "false"}
            ${limit ? `limit: ${limit}` : ``}
            order: order_ASC
        ) {
          total
          items {
            sys{ id firstPublishedAt publishedAt }
            firstname
            lastname
            position
            postalAddress
            email
            image {
              url
              title
              width
              height
            }
            imageSecondary{
              url title width height
            }
            body{ json }
            bodySecondary{ json }
            isHrPageProfile
            dirZone
            codir
            youtubeUrl
          }
        }
      }
      `,
    isDraftMode
  );
  return request.data?.profileCollection?.items as IProfile[];
}

export async function getAllVideoTeamates(
  isDraftMode: boolean = false,
  limit: number
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        teamateVideoCollection(
            preview: ${isDraftMode ? "true" : "false"}
            ${limit ? `limit: ${limit}` : ``}
            order: rank_DESC
        ) {
          total
          items {
            __typename
            sys {
              id
              firstPublishedAt
              publishedAt
            }
            rank
            firstName
            lastName
            projectCity
            projectName
            image {
              url
              title
              width
              height
            }
            video {
              url
              title
              width
              height
            }
          }
        }
      }
    `,
    isDraftMode
  );
  return request.data?.teamateVideoCollection?.items as IVideoProfile[];
}

export async function getCmsGlobalSearchGraphql(
  isDraftMode: boolean = false,
  query: string
  // limit: number
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        postCollection(
            preview: ${isDraftMode ? "true" : "false"}
            where: { title_contains: "${query}" }
            limit:30
        ) {
          total
          items {
            sys{ id }
            __typename
            title
            slug
            intro
            image{ url title }
          }
        }
        operationCollection(
            preview: ${isDraftMode ? "true" : "false"}
            where: { title_contains: "${query}" }
            limit:30
        ) {
          total
          items {
            sys{ id }
            __typename
            title
            slug
            intro
            image{ url title }
          }
        }
        pageCollection(
            preview: ${isDraftMode ? "true" : "false"}
            where: { title_contains: "${query}" }
            limit:30
        ) {
          total
          items {
            sys{ id }
            __typename
            title
            slug
            intro
            image{ url title }
          }
        }
        eventCollection(
            preview: ${isDraftMode ? "true" : "false"}
            where: { title_contains: "${query}" }
            limit:10
        ) {
          total
          items {
            sys{ id }
            __typename
            title
            slug
            intro
            image{ url title }
          }
        }
      }
      `,
    isDraftMode
  );
  return [
    ...(request.data?.operationCollection?.items as IOperation[]),
    ...(request.data?.postCollection?.items as IPost[]),
    ...(request.data?.pageCollection?.items as IPage[]),
    ...(request.data?.eventCollection?.items as IEvent[]),
  ];
}

export async function getCountOperations(
  isDraftMode: boolean = false,
  typeName: string = ``
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        operationCollection(
            preview: ${isDraftMode ? "true" : "false"}
            where: { type_contains_some: ["${typeName}"] }
        ) {
          total
        }
      }
      `,
    isDraftMode
  );
  return { [typeName]: request.data?.operationCollection?.total as number };
}

export async function getAllBanners(
  isDraftMode: boolean = false
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        bannerCollection(
          preview: ${isDraftMode ? "true" : "false"}
          limit: 1
          where: {
            startDate_lte: "${dayjs().format()}"
            endDate_gte: "${dayjs().format()}"
          }
        ) {
          total
          items{
            __typename
            sys{ id publishedAt firstPublishedAt }
            title
            description
            startDate endDate
            image{ url title }
            maxDuration
            targetEntry
            {
              ... on Page {
                sys{ id }
                __typename
                slug
                title
              }
              ... on Post {
                sys{ id }
                __typename
                slug
                title
              }
              ... on Operation {
                sys{ id }
                __typename
                slug
                title
              }
            }
          }
        }
      }
    `,
    isDraftMode
  );
  return request.data?.bannerCollection?.items as IBanner[];
}

export async function getEntriesLinkedFrom(
  isDraftMode: boolean = false,
  entryId: string = ``
): Promise<any> {
  const request = await fetchGraphQL(
    `
      query {
        entryCollection(where: {sys: {id: "${entryId}"}}) {
          items {
            contentfulMetadata {
              __typename
            }
            sys {
              id
              __typename
            }
            ... on Calendar {
              sys {
                id
              }
              linkedFrom {
                entryCollection {
                  items {
                    ... on Page {
                      __typename
                      slug
                    }
                    ... on Post {
                      __typename
                      slug
                    }
                    ... on Operation {
                      __typename
                      slug
                    }
                  }
                }
              }
            }
            ... on Video {
              sys {
                id
              }
              linkedFrom {
                entryCollection {
                  items {
                    ... on Page {
                      __typename
                      slug
                    }
                    ... on Post {
                      __typename
                      slug
                    }
                    ... on Operation {
                      __typename
                      slug
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    isDraftMode
  );
  return request.data.entryCollection.items[0]?.linkedFrom?.entryCollection
    ?.items;
}
